import React, { useState, useEffect, useRef } from 'react';
import { useElephantHunt } from './ElephantHunt';
import emailjs from '@emailjs/browser';
import './TerminalContact.css';

const STAGES = {
    INITIAL: 'INITIAL',
    EMAIL: 'EMAIL',
    SUBJECT: 'SUBJECT',
    MESSAGE: 'MESSAGE',
    SENDING: 'SENDING',
};

const initialLines = [
    { text: 'Connection established.', type: 'system' },
    { text: 'Type "help" for a list of commands.', type: 'system' },
    { text: 'To start, type "send mail" and press Enter.', type: 'system' },
];

const TerminalContact = () => {
    const { discover } = useElephantHunt();
    const [lines, setLines] = useState(initialLines);
    const [input, setInput] = useState('');
    const [stage, setStage] = useState(STAGES.INITIAL);
    const [formData, setFormData] = useState({ email: '', subject: '', message: '' });
    const [isLocked, setIsLocked] = useState(false);


    const terminalBodyRef = useRef(null);
    const inputRef = useRef(null);
    const isInitialMount = useRef(true);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        if (!isLocked && inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, [isLocked]);

    useEffect(() => {
        if (terminalBodyRef.current) {
            terminalBodyRef.current.scrollTop = terminalBodyRef.current.scrollHeight;
        }
    }, [lines]);

    const handleInputChange = (e) => {
        setInput(e.target.value);
    };

    const resetForm = () => {
        setStage(STAGES.INITIAL);
        setFormData({ email: '', subject: '', message: '' });
        setIsLocked(false);
    };

    const sendEmail = () => {
        const templateParams = {
            email: formData.email,
            subject: formData.subject,
            message: formData.message,
        };

        const serviceID = 'service_63p8z4a';
        const templateID = 'template_a67qcun';
        const publicKey = 'Wa_IlKxPZelX5d7sW';

        emailjs.send(serviceID, templateID, templateParams, publicKey)
            .then((response) => {
                setLines(prev => [...prev, { text: `Transmission successful. (Status: ${response.status})`, type: 'success' }, { text: "You can send another message by typing 'send mail'.", type: 'system' }]);
                resetForm();
            })
            .catch((err) => {
                setLines(prev => [...prev, { text: `Transmission failed: ${err.text}`, type: 'error' }, { text: 'Please try again later.', type: 'system' }]);
                resetForm();
            });
    };

    const handleInputSubmit = (e) => {
        e.preventDefault();
        if (!input.trim() || isLocked) return;

        const newLines = [...lines, { text: `> ${input}`, type: 'user' }];
        let nextStage = stage;
        let systemResponse = [];

        switch (stage) {
            case STAGES.INITIAL:
                const command = input.trim().toLowerCase();
                if (['elephant', 'cameron', 'cam'].includes(command)) {
                    discover('terminal');
                    systemResponse.push({ text: '     /  \n ___/ / \n/    o \\___\n|  (   ___/\n|__| |_|', type: 'elephant-art' });
                } else if (command === 'send mail') {
                    nextStage = STAGES.EMAIL;
                    systemResponse.push({ text: 'Enter your email address:', type: 'system' });
                } else if (command === 'help') {
                    systemResponse.push({ text: 'Available commands: "send mail", "linkedin", "github", "clear"', type: 'system' });
                } else if (command === 'linkedin') {
                    systemResponse.push({ text: 'LinkedIn: https://www.linkedin.com/in/kyleperlman/', link: 'https://www.linkedin.com/in/kyleperlman/', type: 'link' });
                } else if (command === 'github') {
                    systemResponse.push({ text: 'GitHub: https://github.com/KPerlman', link: 'https://github.com/KPerlman', type: 'link' });
                } else if (command === 'clear') {
                    setLines([]);
                    setInput('');
                    return;
                }
                else {
                    systemResponse.push({ text: `Command not found: ${input}. Type "help" for commands.`, type: 'error' });
                }
                break;

            case STAGES.EMAIL:
                if (/\S+@\S+\.\S+/.test(input.trim())) {
                    setFormData({ ...formData, email: input.trim() });
                    nextStage = STAGES.SUBJECT;
                    systemResponse.push({ text: 'Enter the subject:', type: 'system' });
                } else {
                    systemResponse.push({ text: 'Invalid email format. Please try again.', type: 'error' });
                }
                break;

            case STAGES.SUBJECT:
                setFormData({ ...formData, subject: input.trim() });
                nextStage = STAGES.MESSAGE;
                systemResponse.push({ text: 'Enter your message. When finished, type "---END---" on a new line.', type: 'system' });
                break;

            case STAGES.MESSAGE:
                if (input.trim().toLowerCase() === '---end---') {
                    nextStage = STAGES.SENDING;
                    setIsLocked(true);
                    systemResponse.push({ text: 'Preparing to send message...', type: 'system' });
                    setLines([...newLines, ...systemResponse]);
                    sendEmail();
                    setInput('');
                    return;
                } else {
                    setFormData(prev => ({ ...prev, message: prev.message + input + '\n' }));
                }
                break;

            default:
                systemResponse.push({ text: 'An unexpected error occurred. Please refresh.', type: 'error' });
                break;
        }

        setLines([...newLines, ...systemResponse]);
        setStage(nextStage);
        setInput('');
    };

    const focusInput = () => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    return (
        <div className="terminal-container" id="contact-terminal">
            <div className="terminal-header">
                <div className="terminal-header-dot red"></div>
                <div className="terminal-header-dot yellow"></div>
                <div className="terminal-header-dot green"></div>
                <span>contact-me@shell</span>
            </div>
            <div className="terminal-body" ref={terminalBodyRef} onClick={focusInput}>
                {lines.map((line, index) => (
                    <p key={index} className={`terminal-line ${line.type}`}>
                        {line.link ? (
                            <a href={line.link} target="_blank" rel="noopener noreferrer">{line.text}</a>
                        ) : (
                            line.text
                        )}
                    </p>
                ))}
                {!isLocked && (
                    <div className="terminal-prompt">
                        <span className="prompt-text">&gt;</span>
                        <form onSubmit={handleInputSubmit}>
                            <input
                                type="text"
                                ref={inputRef}
                                value={input}
                                onChange={handleInputChange}
                                className="terminal-input"
                                aria-label="Terminal command or message"
                                autoComplete="off"
                                disabled={isLocked}
                            />
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TerminalContact;
